import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiChevronLeft, FiChevronRight, FiStar, FiTrendingUp, FiMapPin } = FiIcons;

const TestimonialCarousel = () => {
  const [currentIndex, setCurrentIndex] = useState(0);
  
  const testimonials = [
    {
      id: 1,
      name: "Marco",
      location: "Brera, Milano",
      property: "Attico Moderno 110mq",
      propertyType: "Luxury Penthouse",
      rating: 5,
      text: "Con Castello SRL ho trasformato il rendimento del mio attico in Brera. La gestione è impeccabile e i ricavi sono aumentati del 75% grazie al loro pricing dinamico. Professionalità assoluta.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€2.800/mese",
      afterRevenue: "€4.900/mese",
      increase: "+75%",
      period: "2 anni di collaborazione",
      highlights: ["Gestione H24", "Rating 4.9/5", "Zero pensieri"]
    },
    {
      id: 2,
      name: "Laura",
      location: "Isola, Milano",
      property: "Industrial Loft Design",
      propertyType: "Corporate Loft",
      rating: 5,
      text: "Cercavo un partner serio per il mio loft a Isola. Castello SRL ha superato le aspettative: occupazione costante e ospiti di alto profilo. Una gestione davvero istituzionale.",
      avatar: "https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€1.900/mese",
      afterRevenue: "€3.200/mese",
      increase: "+68%",
      period: "14 mesi di collaborazione",
      highlights: ["Business choice", "92% occupancy", "Staff d'élite"]
    },
    {
      id: 3,
      name: "Giuseppe",
      location: "Cernobbio (Dintorni Milano)",
      property: "Villa Vista Lago",
      propertyType: "Luxury Villa",
      rating: 5,
      text: "Gestire una villa sul lago richiede standard altissimi. Il team di Castello SRL coordina tutto alla perfezione, dalle pulizie alla manutenzione, garantendomi una rendita eccezionale.",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=150&h=150&fit=crop&crop=face",
      beforeRevenue: "€4.500/mese",
      afterRevenue: "€7.800/mese",
      increase: "+73%",
      period: "18 mesi di collaborazione",
      highlights: ["Conciergerie lusso", "ROI superiore", "Target Global"]
    }
  ];

  const nextTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === testimonials.length - 1 ? 0 : prevIndex + 1
    );
  };

  const prevTestimonial = () => {
    setCurrentIndex((prevIndex) => 
      prevIndex === 0 ? testimonials.length - 1 : prevIndex - 1
    );
  };

  useEffect(() => {
    const interval = setInterval(() => {
      nextTestimonial();
    }, 8000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="relative max-w-6xl mx-auto">
      <div className="bg-white rounded-[3rem] shadow-2xl overflow-hidden border border-brand-silver/20">
        <AnimatePresence mode="wait">
          <motion.div 
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="p-8 md:p-16"
          >
            <div className="flex justify-center mb-10">
              {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                <SafeIcon key={i} icon={FiStar} className="h-8 w-8 text-brand-gold fill-current" />
              ))}
            </div>

            <blockquote className="text-2xl md:text-3xl font-light text-brand-blue mb-12 leading-relaxed text-center italic">
              "{testimonials[currentIndex].text}"
            </blockquote>

            <div className="bg-brand-blue/5 rounded-3xl p-8 mb-12 border border-brand-gold/20">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center">
                <div>
                  <div className="text-2xl font-black text-brand-subtle mb-1">{testimonials[currentIndex].beforeRevenue}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest">Rendimento Precedente</div>
                </div>
                <div className="flex items-center justify-center">
                  <div className="bg-brand-gold p-4 rounded-full shadow-lg">
                    <SafeIcon icon={FiTrendingUp} className="h-8 w-8 text-white" />
                  </div>
                </div>
                <div>
                  <div className="text-3xl font-black text-brand-blue mb-1">{testimonials[currentIndex].afterRevenue}</div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-brand-gold">Rendimento Attuale</div>
                </div>
              </div>
              <div className="text-center mt-8">
                <span className="bg-brand-blue text-white px-6 py-2 rounded-xl text-sm font-black uppercase tracking-widest border border-brand-gold">
                  {testimonials[currentIndex].increase} Incremento ROI
                </span>
              </div>
            </div>

            <div className="flex flex-col md:flex-row items-center justify-center space-y-6 md:space-y-0 md:space-x-10">
              <img 
                src={testimonials[currentIndex].avatar} 
                alt={testimonials[currentIndex].name} 
                className="w-24 h-24 rounded-full border-4 border-brand-gold shadow-xl object-cover"
              />
              <div className="text-center md:text-left">
                <div className="font-black text-brand-blue text-2xl uppercase tracking-tight">{testimonials[currentIndex].name}</div>
                <div className="text-brand-gold font-bold uppercase tracking-widest text-xs mb-2">{testimonials[currentIndex].property}</div>
                <div className="text-brand-subtle text-sm flex items-center justify-center md:justify-start">
                  <SafeIcon icon={FiMapPin} className="h-4 w-4 mr-2" />
                  {testimonials[currentIndex].location}
                </div>
              </div>
            </div>
          </motion.div>
        </AnimatePresence>

        <div className="flex justify-between items-center px-12 pb-12">
          <button 
            onClick={prevTestimonial}
            className="bg-brand-blue/5 hover:bg-brand-gold text-brand-blue hover:text-white p-5 rounded-2xl transition-all shadow-md"
          >
            <SafeIcon icon={FiChevronLeft} className="h-6 w-6" />
          </button>

          <div className="flex space-x-4">
            {testimonials.map((_, index) => (
              <button
                key={index}
                onClick={() => setCurrentIndex(index)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${
                  index === currentIndex 
                    ? 'bg-brand-gold scale-150 shadow-lg shadow-brand-gold/50' 
                    : 'bg-brand-silver/50'
                }`}
              />
            ))}
          </div>

          <button 
            onClick={nextTestimonial}
            className="bg-brand-blue/5 hover:bg-brand-gold text-brand-blue hover:text-white p-5 rounded-2xl transition-all shadow-md"
          >
            <SafeIcon icon={FiChevronRight} className="h-6 w-6" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default TestimonialCarousel;